import React from 'react'

import cx from 'classnames'

import { useAssertContext, useConfetti } from '@hooks'

import { SimpleDialog } from '@components/ui'

import type { SuccessDialogContextValue, SuccessDialogProviderProps, SuccessDialogStateProps } from './types'

export const SuccessDialogContext = React.createContext<SuccessDialogContextValue | undefined>(undefined)

export function SuccessDialogProvider({ children }: SuccessDialogProviderProps) {
  const [{ open, title, message, withConfetti, className, ...dialogProps }, setState] =
    React.useState<SuccessDialogStateProps>({
      open: false,
      title: '',
      message: '',
      withConfetti: false,
    })

  const { triggerConfetti } = useConfetti({
    spread: 90,
    particleCount: 100,
    origin: { y: 0.5 },
  })

  React.useEffect(() => {
    if (open && withConfetti) {
      triggerConfetti()
    }
  }, [open, triggerConfetti, withConfetti])

  const openDialog: SuccessDialogContextValue = React.useCallback(
    payload => setState(prevState => ({ ...prevState, ...payload, open: true })),
    []
  )

  const closeDialog = () => setState(prevState => ({ ...prevState, open: false }))

  return (
    <SuccessDialogContext.Provider value={openDialog}>
      {children}
      <SimpleDialog
        className={cx('gap-3x', className)}
        description={message}
        id="success-response-dialog"
        onClose={closeDialog}
        onPrimaryAction={closeDialog}
        open={open}
        shouldReturnFocusAfterClose={false}
        size="xs"
        title={title}
        {...dialogProps}
      />
    </SuccessDialogContext.Provider>
  )
}

export function useSuccessDialog(): SuccessDialogContextValue {
  return useAssertContext(SuccessDialogContext)
}
